import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { UserPlus, Trash2, Shield, Eye, Users } from 'lucide-react';
import Card from './components/Card';
import Button from './components/Button';
import Badge from './components/Badge';
import { useToast } from './ToastContext';
import {
  getProjectMembers,
  getProjectMemberCandidates,
  inviteProjectMember,
  updateProjectMemberRole,
  removeProjectMember,
} from './api';

const ROLE_LABELS = {
  admin: { label: 'Admin', color: 'accent', icon: Shield },
  viewer: { label: 'Lecteur', color: 'info', icon: Eye },
};

function MemberAvatar({ member }) {
  if (member.avatar) {
    return (
      <img
        src={member.avatar}
        alt={`${member.first_name} ${member.last_name}`}
        className="w-9 h-9 rounded-full object-cover border border-white/20"
      />
    );
  }
  const initials = `${member.first_name?.[0] ?? ''}${member.last_name?.[0] ?? ''}`.toUpperCase();
  return (
    <div className="w-9 h-9 rounded-full bg-accent/20 text-accent flex items-center justify-center text-sm font-semibold border border-accent/30">
      {initials}
    </div>
  );
}

export default function ProjectMembers({ project: projectProp } = {}) {
  const { slug: slugParam } = useParams();
  const slug = projectProp?.slug ?? slugParam;
  const { showToast } = useToast();

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteQuery, setInviteQuery] = useState('');
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [showCandidates, setShowCandidates] = useState(false);
  const [inviteRole, setInviteRole] = useState('viewer');
  const [inviting, setInviting] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  const fetchMembers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getProjectMembers(slug);
      setMembers(data);
    } catch (err) {
      if (err?.response?.status !== 403) {
        showToast('Impossible de charger les membres.', 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [slug, showToast]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const fetchCandidates = useCallback(async (query) => {
    try {
      setLoadingCandidates(true);
      const data = await getProjectMemberCandidates(slug, query, 12);
      setCandidates(data);
    } catch {
      setCandidates([]);
    } finally {
      setLoadingCandidates(false);
    }
  }, [slug]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchCandidates(inviteQuery);
    }, 180);
    return () => clearTimeout(timer);
  }, [inviteQuery, fetchCandidates]);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!selectedCandidate?.email) {
      showToast("Sélectionnez d'abord un utilisateur dans la liste.", 'warning');
      return;
    }
    setInviting(true);
    try {
      const newMember = await inviteProjectMember(slug, selectedCandidate.email, inviteRole);
      setMembers((prev) => [...prev, newMember]);
      setInviteQuery('');
      setSelectedCandidate(null);
      setCandidates((prev) => prev.filter((c) => c.user_id !== newMember.user_id));
      showToast(`${newMember.first_name} ${newMember.last_name} a été ajouté(e) au projet.`, 'success');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === 'Aucun utilisateur trouvé avec cet email.') {
        showToast('Aucun compte trouvé avec cet email.', 'error');
      } else if (detail === 'Cet utilisateur est déjà membre du projet.') {
        showToast('Cet utilisateur est déjà membre du projet.', 'warning');
      } else {
        showToast(detail || "Erreur lors de l'invitation.", 'error');
      }
    } finally {
      setInviting(false);
    }
  };

  const handleRoleChange = async (member, newRole) => {
    try {
      await updateProjectMemberRole(slug, member.user_id, newRole);
      setMembers((prev) =>
        prev.map((m) => (m.user_id === member.user_id ? { ...m, role: newRole } : m))
      );
      showToast(`Rôle de ${member.first_name} mis à jour.`, 'success');
    } catch {
      showToast('Impossible de modifier le rôle.', 'error');
    }
  };

  const handleRemove = async (member) => {
    setRemovingId(member.user_id);
    try {
      await removeProjectMember(slug, member.user_id);
      setMembers((prev) => prev.filter((m) => m.user_id !== member.user_id));
      showToast(`${member.first_name} ${member.last_name} a été retiré(e) du projet.`, 'success');
    } catch {
      showToast('Impossible de retirer ce membre.', 'error');
    } finally {
      setRemovingId(null);
    }
  };

  const project = projectProp;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-heading font-semibold text-blue-gray-900 flex items-center gap-2">
          <Users size={20} className="text-accent" />
          Équipe du projet
        </h2>
        <p className="text-sm text-blue-gray-400 mt-1">
          Invitez des collaborateurs à accéder aux audits de ce projet.
        </p>
      </div>

      {/* Formulaire d'invitation */}
      <Card className="p-5">
        <h3 className="text-sm font-heading font-semibold text-blue-gray-900 mb-4">Inviter un membre</h3>
        <form onSubmit={handleInvite} className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <input
              type="text"
              value={inviteQuery}
              onChange={(e) => {
                setInviteQuery(e.target.value);
                setSelectedCandidate(null);
                setShowCandidates(true);
              }}
              onFocus={() => setShowCandidates(true)}
              onBlur={() => {
                setTimeout(() => setShowCandidates(false), 120);
              }}
              placeholder="Rechercher un utilisateur (email, prénom, nom)..."
              required
              className="w-full bg-white border border-blue-gray-200 rounded-md px-3 py-2 text-sm text-blue-gray-900 placeholder:text-blue-gray-400 focus:outline-none focus:ring-2 focus:ring-accent/40"
            />
            {showCandidates && (
              <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-blue-gray-200 rounded-md shadow-lg max-h-56 overflow-y-auto">
                {loadingCandidates ? (
                  <div className="px-3 py-2 text-xs text-blue-gray-500">Chargement...</div>
                ) : candidates.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-blue-gray-500">Aucun utilisateur disponible.</div>
                ) : (
                  candidates.map((candidate) => (
                    <button
                      key={candidate.user_id}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setSelectedCandidate(candidate);
                        setInviteQuery(`${candidate.first_name} ${candidate.last_name} (${candidate.email})`);
                        setShowCandidates(false);
                      }}
                      className="w-full text-left px-3 py-2 hover:bg-blue-gray-50 transition border-b last:border-b-0 border-blue-gray-100"
                    >
                      <div className="text-sm text-blue-gray-900">
                        {candidate.first_name} {candidate.last_name}
                      </div>
                      <div className="text-xs text-blue-gray-500">{candidate.email}</div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
          <select
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value)}
            className="bg-white border border-blue-gray-200 rounded-md px-3 py-2 text-sm text-blue-gray-900 focus:outline-none focus:ring-2 focus:ring-accent/40"
          >
            <option value="viewer">Lecteur</option>
            <option value="admin">Admin</option>
          </select>
          <Button type="submit" disabled={inviting || !selectedCandidate} className="shrink-0">
            <UserPlus size={15} className="mr-1.5" />
            {inviting ? 'Envoi…' : 'Inviter'}
          </Button>
        </form>
        <div className="mt-3 text-xs text-blue-gray-500 space-y-0.5">
          <p><span className="text-accent font-medium">Admin</span> — peut lancer des audits et modifier les paramètres du projet.</p>
          <p><span className="text-info font-medium">Lecteur</span> — peut consulter les audits (lecture seule).</p>
        </div>
      </Card>

      {/* Liste des membres */}
      <Card className="p-5">
        <h3 className="text-sm font-heading font-semibold text-blue-gray-900 mb-4">
          Membres ({members.length})
        </h3>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-accent border-t-transparent rounded-full animate-spin" />
          </div>
        ) : members.length === 0 ? (
          <div className="text-center py-8 text-blue-gray-500 text-sm">
            <Users size={32} className="mx-auto mb-2 opacity-30" />
            Aucun membre pour l'instant. Invitez des collaborateurs ci-dessus.
          </div>
        ) : (
          <ul className="divide-y divide-blue-gray-100">
            {members.map((member) => {
              const roleInfo = ROLE_LABELS[member.role] ?? ROLE_LABELS.viewer;
              const RoleIcon = roleInfo.icon;
              return (
                <li key={member.user_id} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                  <MemberAvatar member={member} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-blue-gray-900 truncate">
                      {member.first_name} {member.last_name}
                    </p>
                    <p className="text-xs text-blue-gray-400 truncate">{member.email}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <select
                      value={member.role}
                      onChange={(e) => handleRoleChange(member, e.target.value)}
                      className="bg-white border border-blue-gray-200 rounded px-2 py-1 text-xs text-blue-gray-900 focus:outline-none focus:ring-1 focus:ring-accent/40"
                      title="Changer le rôle"
                    >
                      <option value="viewer">Lecteur</option>
                      <option value="admin">Admin</option>
                    </select>
                    <Badge color={roleInfo.color} className="hidden sm:inline-flex items-center gap-1">
                      <RoleIcon size={10} />
                      {roleInfo.label}
                    </Badge>
                    <button
                      onClick={() => handleRemove(member)}
                      disabled={removingId === member.user_id}
                      title="Retirer du projet"
                      className="p-1.5 rounded text-blue-gray-500 hover:text-error hover:bg-error/10 transition disabled:opacity-40"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
}
